// Reconciler parity harness (Phase A, A4). Dev-only drift DETECTOR: after every
// fold, the client's rendered value for each server-owned, publicly-projected field
// must equal the projection. A mismatch is a reconciler gap. A server field with no
// fold, or a fold that diverged. It WARNS, never throws, so drift is surfaced to
// developers without breaking a live session. As Phase B server breadth grows this
// catches missing folds automatically instead of by hand.
//
// Scope: publicly-projected, synchronously-folded fields on IN-PLAY cards (stats /
// flags both seats see), plus the ledger + resource totals. Position + board-entry
// (a play-zone card with no mesh yet) are intentionally excluded. Those still ride
// the legacy relay pre-P8, so their "drift" is a known gap, not a fold bug.
import { isDevBuild } from '../../../utils/devMode';

const CARD_FIELDS = ['tapped', 'rotated', 'currentLife', 'currentAttack', 'subsurface'];

function arrDrift(a, b) {
  return JSON.stringify([...(a || [])].sort()) !== JSON.stringify([...(b || [])].sort());
}

// Pure: returns a list of human-readable drift descriptions (empty = in parity).
export function computeReconcilerDrift(host, mirror) {
  const drift = [];
  if (!mirror?.cards) return drift;

  for (const [id, mc] of Object.entries(mirror.cards)) {
    if (mc.zone !== 'play') continue; // only board cards render stats/flags
    const card = host.meshes?.get(id)?.userData?.cardInstance;
    if (!card) continue; // no mesh yet = a legacy-relay board-entry gap, not a fold bug
    for (const f of CARD_FIELDS) {
      if (mc[f] !== undefined && card[f] !== mc[f]) drift.push(`${id}.${f} render=${card[f]} server=${mc[f]}`);
    }
    if (mc.statuses !== undefined && arrDrift(card.statuses, mc.statuses)) {
      drift.push(`${id}.statuses render=${JSON.stringify(card.statuses)} server=${JSON.stringify(mc.statuses)}`);
    }
    if (mc._providesThisTurn !== undefined
      && JSON.stringify(mc._providesThisTurn) !== JSON.stringify(card._providesThisTurn ?? null)) {
      drift.push(`${id}._providesThisTurn render=${JSON.stringify(card._providesThisTurn ?? null)} server=${JSON.stringify(mc._providesThisTurn)}`);
    }
  }

  const mLed = mirror.effectLedger?.entries?.length ?? 0;
  const cLed = host.effectLedger?.entries?.length ?? 0;
  if (mLed !== cLed) drift.push(`effectLedger count render=${cLed} server=${mLed}`);

  const tr = host.state?.gameState?.trackers || {};
  for (const seat of ['p1', 'p2']) {
    const life = mirror.players?.[seat]?.life;
    if (life !== undefined && tr[seat] && tr[seat].life !== life) drift.push(`${seat}.life render=${tr[seat].life} server=${life}`);
  }

  return drift;
}

export function assertReconcilerParity(host, mirror) {
  if (!isDevBuild() || host.props?.isCasualSandbox) return;
  // Replay: seek Fulls interleave with render flushes, so transient drift is
  // expected and meaningless here (same rationale as the sandbox skip).
  if (host.props?.replayDriver) return;
  const drift = computeReconcilerDrift(host, mirror);
  if (drift.length) {
    console.warn(`[reconciler-parity] ${drift.length} drift(s) after fold:\n  ${drift.join('\n  ')}`);
  }
}
